import { styled } from '@mui/material';
import { type ReactNode, useId } from 'react';

type Props = {
  children: ReactNode;
  /** Visible group name, e.g. "Suivi". Names the list for assistive technologies. */
  title?: string;
  /** Names the list when several navigation groups share a landmark and no title is shown. */
  'aria-label'?: string;
  'data-testid'?: string;
};

const Group = styled('div')(({ theme }) => ({
  '& + &': {
    marginTop: theme.spacing(2),
  },
}));

const Title = styled('span')(({ theme }) => ({
  ...theme.typography.body2Emphasis,
  display: 'block',
  paddingInline: theme.spacing(1.5),
  paddingBottom: theme.spacing(0.5),
  color: theme.palette.text.secondary,
}));

const List = styled('ul')(({ theme }) => ({
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}));

/** Vertical list of `NavigationListItem` links, e.g. the main navigation of an `AppShell`. Titled lists stack as groups. */
export function NavigationList(props: Props) {
  const titleId = useId();
  const list = (
    <List
      aria-label={props.title ? undefined : props['aria-label']}
      aria-labelledby={props.title ? titleId : undefined}
      data-testid={props['data-testid'] ?? 'navigation-list'}
    >
      {props.children}
    </List>
  );

  if (!props.title) {
    return list;
  }

  return (
    <Group>
      <Title id={titleId}>{props.title}</Title>
      {list}
    </Group>
  );
}

export type { Props as NavigationListProps };
